<template>
  <div class="px-[35px] pt-4 pb-[35px] w-full max-w-[1061px] h-full flex flex-col overflow-y-auto hide-scrollbar">
    <!-- Actions Bar (fPoPW) -->
    <div class="flex justify-between items-center mb-[14px] h-[44px] shrink-0">
      <!-- Filter Tabs -->
      <div class="flex items-center gap-[6px]">
        <button
          v-for="pill in filterPills"
          :key="pill.value"
          class="rounded-[20px] font-['Inter',sans-serif] text-[13px] flex items-center transition-colors"
          :class="[
            activeFilter === pill.value
              ? 'bg-[#043f42] text-white font-semibold px-[18px] py-[8px]'
              : 'border border-gray-900 text-gray-500 font-normal hover:bg-gray-50 px-[14px] py-[8px]'
          ]"
          @click="activeFilter = pill.value"
        >
          {{ pill.label }}
        </button>
      </div>

      <!-- Right Actions -->
      <div class="flex items-center gap-4">
        <!-- Time Filter -->
        <div class="relative">
          <select
            v-model="dateRange"
            class="appearance-none bg-transparent border border-gray-900 rounded-[20px] pl-[14px] pr-7 py-[8px] text-[13px] text-gray-700 font-['Inter',sans-serif] cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#043f42]"
          >
            <option value="30days">
              Last 30 days
            </option>
            <option value="today">
              Today
            </option>
            <option value="7days">
              Last 7 days
            </option>
            <option value="all">
              All time
            </option>
          </select>
          <div class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700 text-[12px]">
            &darr;
          </div>
        </div>

        <button
          :disabled="isMarkingRead || unreadCount === 0"
          class="text-[#0D9488] font-['Inter',sans-serif] text-[13px] hover:text-[#0f766e] transition-colors disabled:opacity-50 cursor-pointer"
          @click="handleMarkAllRead"
        >
          Mark all as read
        </button>
      </div>
    </div>

    <!-- Notification Card (vK2I0) -->
    <div class="bg-[#F3F4F6] rounded-[25px] overflow-hidden flex flex-col gap-[1px] mb-[11px]">
      <!-- Card Header -->
      <div class="h-[60px] bg-[#F9FAFB] px-8 flex items-center gap-3 shrink-0">
        <div class="bg-blue-100 rounded-[20px] px-2.5 py-1 flex items-center">
          <span class="text-blue-800 font-['Inter',sans-serif] text-[11px] font-bold">{{ unreadCount }} unread</span>
        </div>
        <span class="text-gray-400 font-['Inter',sans-serif] text-[13px]">{{ totalCount }} total</span>
      </div>

      <!-- List -->
      <div class="bg-white flex flex-col min-h-[380px]">
        <div
          v-if="isLoading && pageNotifications.length === 0"
          class="p-8 text-center text-gray-500"
        >
          Loading notifications...
        </div>

        <div
          v-else-if="filteredNotifications.length === 0"
          class="p-12 text-center flex flex-col items-center justify-center gap-3 min-h-[380px]"
        >
          <p class="text-gray-500 font-['Inter',sans-serif] text-[14px]">
            {{ emptyStateMessage }}
          </p>
          <button
            v-if="activeFilter !== 'all' || dateRange !== '30days'"
            class="px-4 py-2 bg-[#043f42] text-white rounded-[20px] text-[13px] font-medium hover:bg-[#032e30] transition-colors cursor-pointer"
            @click="clearFilters"
          >
            Clear filters
          </button>
        </div>

        <template v-else>
          <NotificationRow
            v-for="notification in filteredNotifications"
            :key="notification.id"
            :notification="notification"
            :compact="false"
          />
        </template>
      </div>
    </div>

    <!-- Pagination (Ix6ZQ) -->
    <div
      v-if="totalPages > 1"
      class="px-[9px] h-[60px] flex justify-between items-center w-full shrink-0 mt-auto pb-2"
    >
      <span class="text-[#9CA3AF] text-[13px] font-['Inter',sans-serif]">
        Showing {{ (currentPage - 1) * perPage + 1 }}–{{ Math.min(currentPage * perPage, totalCount) }} of {{ totalCount }} notifications
      </span>
      <div class="flex items-center gap-2">
        <button
          :disabled="currentPage === 1"
          class="w-[32px] h-[32px] rounded-full border border-gray-300 flex items-center justify-center transition-colors text-gray-800 hover:bg-gray-100 cursor-pointer disabled:opacity-50 disabled:text-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:hover:bg-gray-50"
          @click="prevPage"
        >
          <svg
            class="w-[18px] h-[18px]"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              cx="12"
              cy="12"
              r="9.5"
              stroke="currentColor"
              stroke-width="1.2"
            />
            <path
              d="M13.5 8.5L9.5 12L13.5 15.5"
              stroke="currentColor"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button
          :disabled="currentPage === totalPages"
          class="w-[32px] h-[32px] rounded-full border border-gray-300 flex items-center justify-center transition-colors text-gray-800 hover:bg-gray-100 cursor-pointer disabled:opacity-50 disabled:text-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:hover:bg-gray-50"
          @click="nextPage"
        >
          <svg
            class="w-[18px] h-[18px]"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              cx="12"
              cy="12"
              r="9.5"
              stroke="currentColor"
              stroke-width="1.2"
            />
            <path
              d="M10.5 8.5L14.5 12L10.5 15.5"
              stroke="currentColor"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useNotifications } from '../../composables/useNotifications'
import NotificationRow from './NotificationRow.vue'

const { unreadCount, totalCount, markAllRead, fetchRecent } = useNotifications()

const pageNotifications = ref([])
const currentPage = ref(1)
const isLoading = ref(false)
const perPage = 5
let pollInterval = null
let latestFetchId = 0

const activeFilter = ref('all')
const dateRange = ref('30days')

const filterPills = [
  { label: 'All', value: 'all' },
  { label: 'Signed', value: 'signed' },
  { label: 'Declined', value: 'declined' },
  { label: 'Pending', value: 'pending' },
  { label: 'Completed', value: 'completed' }
]

const fetchPage = async () => {
  const currentFetchId = ++latestFetchId
  const offset = Math.max(0, (currentPage.value - 1) * perPage)
  isLoading.value = true
  try {
    const response = await fetch(`/api/notifications?limit=${perPage}&offset=${offset}`, {
      headers: { Accept: 'application/json' }
    })
    if (response.ok) {
      if (currentFetchId !== latestFetchId) return
      const data = await response.json()
      pageNotifications.value = Array.isArray(data) ? data : []
    }
  } catch (e) {
    console.error('Failed to fetch page notifications', e)
  } finally {
    if (currentFetchId === latestFetchId) {
      isLoading.value = false
    }
  }
}

const handleMarkAllRead = async () => {
  const previousState = JSON.stringify(pageNotifications.value)
  try {
    pageNotifications.value.forEach(n => {
      n.read = true
    })
    await markAllRead()
  } catch (e) {
    pageNotifications.value = JSON.parse(previousState)
  }
}

const clearFilters = () => {
  activeFilter.value = 'all'
  dateRange.value = '30days'
  currentPage.value = 1
}

const filteredNotifications = computed(() => {
  return pageNotifications.value.filter(n => {
    // Event Type Filter
    if (activeFilter.value !== 'all') {
      const type = n.type || ''
      if (activeFilter.value === 'signed' && !type.includes('sign') && type !== 'folio.completed') return false
      if (activeFilter.value === 'declined' && !type.includes('decline') && !type.includes('cancel') && type !== 'folio.declined') return false
      if (activeFilter.value === 'pending' && !type.includes('pending') && !type.includes('awaiting')) return false
      if (activeFilter.value === 'completed' && (!type.includes('complete') || type === 'folio.completed') && !type.includes('cohort')) return false
    }

    // Date Range Filter
    if (dateRange.value !== 'all' && n.timestamp) {
      const date = new Date(n.timestamp)
      const now = new Date()
      const diffMs = now - date
      const diffDays = diffMs / (1000 * 60 * 60 * 24)

      if (dateRange.value === 'today' && diffDays > 1) return false
      if (dateRange.value === '7days' && diffDays > 7) return false
      if (dateRange.value === '30days' && diffDays > 30) return false
    }

    return true
  })
})

const emptyStateMessage = computed(() => {
  if (activeFilter.value !== 'all' || dateRange.value !== '30days') {
    return 'No notifications found for the selected filters.'
  }
  return 'You are all caught up.'
})

const totalPages = computed(() => Math.ceil(totalCount.value / perPage) || 1)

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
    fetchPage()
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
    fetchPage()
  }
}

onMounted(() => {
  fetchRecent()
  fetchPage()
  // AC9: poll every 30s for the page
  pollInterval = setInterval(fetchPage, 30000)
})

onUnmounted(() => {
  if (pollInterval) {
    clearInterval(pollInterval)
  }
})
</script>
